/**
 * JobRoleLanding — niche job-search SEO landing page
 *
 * Renders live job listings for a given role slug (e.g. "identity-management-consultant"),
 * paired with the Free ATS Check CTA. The slug is treated as a human-readable role title
 * (slug → title case) and dispatched directly to /api/jobs/search, which already does
 * semantic LLM expansion + parallel JSearch/Adzuna fetch + location relaxation.
 *
 * Why this page exists:
 *   - Niche role queries get long-tail SEO traffic that a generic job board can't capture
 *   - Our broadened_queries_log telemetry surfaces real org queries that get re-routed
 *   - Each landing converts long-tail search visitors into ATS-Check signups
 *
 * SEO is handled by /app/frontend/seo-routes.js entries + prerender.js runtime injector.
 * This component does NOT manage <head> tags directly.
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowRight, Briefcase, ExternalLink, Sparkles, MapPin, CheckCircle2 } from 'lucide-react';
import { logger } from '../utils/logger';
import { useDocumentMeta } from '../utils/useDocumentMeta';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } };

// Whitelist of slugs we're confident render meaningful results. Must match
// the slugs in /app/frontend/seo-routes.js so prerender + sitemap stay in sync.
// Each entry carries the full SEO copy so the page renders correct <title>
// and meta description on first paint in BOTH preview and production envs
// (preview misses the build-time prerender pass; production gets both).
const SUPPORTED_SLUGS = {
  'identity-management-consultant': {
    title: 'Identity Management Consultant',
    metaTitle: 'Identity Management Consultant Jobs (Live) + Free ATS Resume Score | Ascend',
    description: 'IAM Consultant, IDM Consultant, Identity & Access Management — live US jobs + free ATS resume score for cybersecurity identity roles.',
  },
  'cybersecurity-engineer': {
    title: 'Cybersecurity Engineer',
    metaTitle: 'Cybersecurity Engineer Jobs (Live) + Free ATS Resume Score | Ascend',
    description: 'SecOps, InfoSec & Cybersecurity Engineer roles — live US jobs + free ATS score tuned for security clearances and certifications (CISSP, SOC2, NIST).',
  },
  'machine-learning-engineer': {
    title: 'Machine Learning Engineer',
    metaTitle: 'Machine Learning Engineer Jobs (Live) + Free ATS Resume Score | Ascend',
    description: 'MLOps, ML Engineer, Applied Scientist — live US ML jobs + free ATS resume score tuned for PyTorch, TensorFlow, and production ML stacks.',
  },
  'site-reliability-engineer': {
    title: 'Site Reliability Engineer',
    metaTitle: 'Site Reliability Engineer Jobs (Live) + Free ATS Resume Score | Ascend',
    description: 'SRE, Platform Engineer & DevOps roles — live US jobs + free ATS resume score tuned for Kubernetes, Terraform, and on-call SRE workflows.',
  },
  'cloud-solutions-architect': {
    title: 'Cloud Solutions Architect',
    metaTitle: 'Cloud Solutions Architect Jobs (Live) + Free ATS Resume Score | Ascend',
    description: 'AWS, Azure & GCP Solutions Architect roles — live US jobs + free ATS resume score tuned for cloud certifications and reference architectures.',
  },
};

const Section = ({ children, className = '' }) => (
  <section className={`max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>{children}</section>
);

const JobRoleLanding = () => {
  const { slug } = useParams();
  const meta = SUPPORTED_SLUGS[slug];
  const roleTitle = meta?.title;

  useDocumentMeta({
    title: meta?.metaTitle,
    description: meta?.description,
    path: slug ? `/jobs/role/${slug}` : undefined,
    imageAlt: roleTitle ? `${roleTitle} jobs live listings with free ATS resume score and keyword analysis` : undefined,
  });

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [variants, setVariants] = useState([]);

  useEffect(() => {
    if (!roleTitle) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/jobs/search`, { params: { q: roleTitle } });
        if (cancelled) return;
        setJobs((res.data.jobs || []).slice(0, 12));
        setVariants(res.data.expansion_variants || []);
      } catch (err) {
        logger.error('JobRoleLanding fetch failed:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [roleTitle]);

  if (!roleTitle) return <Navigate to="/jobs" replace />;

  return (
    <div className="min-h-screen bg-white" data-testid={`job-role-landing-${slug}`}>
      {/* Nav */}
      <nav className="border-b border-[#e5e7eb] bg-white sticky top-0 z-50">
        <Section className="flex items-center justify-between py-4">
          <Link to="/" className="font-satoshi text-xl font-black tracking-tight text-[#0f1115]" data-testid="logo-home">
            ASCEND
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/free-ats-check" className="text-sm text-[#4b5563] hover:text-[#002fa7] transition-colors hidden sm:block" data-testid="nav-free-ats">
              Free ATS Check
            </Link>
            <Link
              to="/free-ats-check"
              className="bg-[#002fa7] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#001f6f] transition-colors"
              data-testid="nav-cta-free-ats"
            >
              Score My Resume Free
            </Link>
          </div>
        </Section>
      </nav>

      {/* Hero */}
      <Section className="py-12 sm:py-16">
        <motion.div {...fadeUp} transition={{ duration: 0.5 }} className="max-w-3xl">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#eff6ff] text-[#1e3a8a] text-xs font-semibold border border-[#bfdbfe]">
            <Briefcase className="w-3.5 h-3.5" /> Live Jobs · Updated Hourly
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-satoshi font-black tracking-tight text-[#0f1115] mt-4">
            {roleTitle} Jobs
            <span className="block text-[#002fa7]">Live Listings + Free ATS Score</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-[#4b5563] leading-relaxed">
            Real openings from JSearch &amp; Adzuna for <strong>{roleTitle}</strong> roles, pulled live every search.
            Run a free ATS resume score below — see exactly which keywords from these listings are missing from your resume.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link
              to="/free-ats-check"
              className="inline-flex items-center gap-2 bg-[#002fa7] text-white px-6 py-3 rounded-lg text-sm font-semibold hover:bg-[#001f6f] transition-colors"
              data-testid="hero-cta-free-ats"
            >
              <Sparkles className="w-4 h-4" /> Get My Free ATS Score
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to={`/jobs?q=${encodeURIComponent(roleTitle)}`}
              className="inline-flex items-center gap-2 border border-[#e5e7eb] text-[#0f1115] px-6 py-3 rounded-lg text-sm font-semibold hover:bg-[#f9fafb] transition-colors"
              data-testid="hero-cta-job-board"
            >
              Open Full Job Board <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {variants.length > 1 && (
            <p className="mt-4 text-xs text-[#6b7280]">
              We also search related titles:{' '}
              {variants.slice(1, 5).map((v, i, arr) => (
                <React.Fragment key={v}>
                  <span className="font-medium text-[#374151]">{v}</span>
                  {i < arr.length - 1 ? ', ' : ''}
                </React.Fragment>
              ))}
            </p>
          )}
        </motion.div>
      </Section>

      {/* Live Jobs */}
      <Section className="py-8 border-t border-[#e5e7eb]">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl sm:text-3xl font-satoshi font-bold text-[#0f1115]">
            Live {roleTitle} Openings
          </h2>
          <Link to={`/jobs?q=${encodeURIComponent(roleTitle)}`} className="text-sm font-semibold text-[#002fa7] hover:underline hidden sm:inline" data-testid="see-all-jobs-link">
            See all →
          </Link>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="job-role-landing-loading">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-32 rounded-lg bg-[#f9fafb] border border-[#e5e7eb] animate-pulse" />
            ))}
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-12 bg-[#f9fafb] rounded-lg border border-[#e5e7eb]" data-testid="job-role-landing-empty">
            <p className="text-[#6b7280]">No live listings right now — try the full job board for broader results.</p>
            <Link to="/jobs" className="inline-block mt-3 text-sm font-semibold text-[#002fa7] hover:underline">
              Open Job Board →
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="job-role-landing-results">
            {jobs.map((job, i) => (
              <a
                key={`${job.url}-${i}`}
                href={job.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block p-5 rounded-lg border border-[#e5e7eb] hover:border-[#002fa7] hover:shadow-md transition-all bg-white"
                data-testid={`job-role-landing-card-${i}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-[#0f1115] line-clamp-2">{job.title}</h3>
                    <p className="text-sm text-[#4b5563] mt-1 truncate">{job.company || 'Confidential'}</p>
                    {job.location && (
                      <p className="text-xs text-[#6b7280] mt-1 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {job.location}
                      </p>
                    )}
                  </div>
                  <ExternalLink className="w-4 h-4 text-[#6b7280] flex-shrink-0" />
                </div>
                {job.source && (
                  <p className="text-[10px] uppercase tracking-wide text-[#9ca3af] mt-3">via {job.source}</p>
                )}
              </a>
            ))}
          </div>
        )}
      </Section>

      {/* CTA strip */}
      <Section className="py-16">
        <div className="bg-gradient-to-br from-[#0f1115] to-[#1f2937] text-white rounded-2xl p-8 sm:p-12 text-center" data-testid="cta-strip">
          <h2 className="text-2xl sm:text-3xl font-satoshi font-bold">
            Land more {roleTitle} interviews
          </h2>
          <p className="mt-3 text-[#d1d5db] max-w-xl mx-auto">
            Run a free 30-second ATS check. See exactly which keywords from real {roleTitle} job postings are missing from your resume.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/free-ats-check"
              className="inline-flex items-center gap-2 bg-white text-[#0f1115] px-6 py-3 rounded-lg text-sm font-bold hover:bg-[#f3f4f6] transition-colors"
              data-testid="footer-cta-free-ats"
            >
              <Sparkles className="w-4 h-4" /> Score My Resume Free
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <p className="mt-4 text-xs text-[#9ca3af] flex items-center justify-center gap-2">
            <CheckCircle2 className="w-3.5 h-3.5" /> No signup · No credit card · 30 seconds
          </p>
        </div>
      </Section>

      {/* Footer */}
      <footer className="border-t border-[#e5e7eb] py-8 mt-8">
        <Section>
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-[#6b7280]">
            <span>© 2026 Ascend. Live job data from JSearch &amp; Adzuna.</span>
            <div className="flex items-center gap-4">
              <Link to="/jobs" className="hover:text-[#002fa7]">All Jobs</Link>
              <Link to="/blog" className="hover:text-[#002fa7]">Blog</Link>
              <Link to="/free-ats-check" className="hover:text-[#002fa7]">Free ATS Check</Link>
            </div>
          </div>
        </Section>
      </footer>
    </div>
  );
};

export default JobRoleLanding;
